import { useEffect, useState } from 'react';
import { useMutation, useQuery, type QueryKey } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import type { DevicePreset, DevicePresetPlan, Page } from '@simpleacs/shared';
import { api } from '../../lib/api';
import { useTasks } from '../../lib/tasks';
import { WizardDialog } from '../../components/WizardDialog';
import { Button, ErrorNotice, Field, Pill, Spinner } from '../../components/ui';

/**
 * Apply a saved device preset to THIS ONU.
 *
 * One subscriber, named by the page it was opened from, and nothing is written
 * until the operator has seen the exact commands or parameter writes — the same
 * two-step every other write on this platform makes. There is deliberately no
 * "apply to all like this one" here: bulk is a separate, separately-guarded
 * thing, and a dialog that quietly grew a checkbox list is how it stops being
 * separate.
 *
 * A preset for the wrong management mode or the wrong vendor comes back with
 * `blockers` and the write button stays disabled, rather than being sent and
 * refused halfway down a chassis session.
 */
export function ApplyDevicePresetDialog({
  onuId,
  onuSerial,
  managementMode,
  onClose,
  href,
  invalidate,
}: {
  onuId: string;
  onuSerial: string;
  managementMode: 'tr069' | 'omci' | null;
  onClose: () => void;
  /** Where the background toast/tray row links — the ONU's own page. */
  href?: string;
  /** Extra query keys to refresh once the write lands (the ONU page's own). */
  invalidate?: QueryKey[];
}) {
  const tasks = useTasks();
  const [presetId, setPresetId] = useState('');
  const [plan, setPlan] = useState<DevicePresetPlan | null>(null);
  const [error, setError] = useState<string | null>(null);

  const presets = useQuery({
    queryKey: ['device-presets'],
    queryFn: () => api.get<Page<DevicePreset>>('/device-presets'),
  });
  const items = presets.data?.items ?? [];

  /**
   * Default to a preset that speaks this ONU's own management mode. It is only
   * a default — an operator may deliberately pick the other one, and the plan
   * will say what happens if they do.
   */
  useEffect(() => {
    if (presetId || items.length === 0) return;
    const matching = items.find((p) => p.target === managementMode);
    setPresetId((matching ?? items[0]!).id);
  }, [items, managementMode, presetId]);

  const chosen = items.find((p) => p.id === presetId) ?? null;
  const path = presetId ? `/device-presets/${encodeURIComponent(presetId)}/apply` : '';

  const check = useMutation({
    mutationFn: () => api.post<DevicePresetPlan>(path, { onuId, dryRun: true }),
    onSuccess: (r) => {
      setPlan(r);
      setError(null);
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Could not build the plan'),
  });

  /**
   * Write hands the chassis/ACS work to the background tray and closes — like
   * Resync. The preview above stays in the dialog; only the write moves
   * off-screen so the operator is not pinned while a session runs.
   */
  const startApply = () => {
    const presetName = chosen?.name ?? 'preset';
    onClose();
    tasks.start<DevicePresetPlan>({
      label: `Apply “${presetName}” — ${onuSerial}`,
      ...(href ? { href } : {}),
      run: () => api.post<DevicePresetPlan>(path, { onuId, dryRun: false }),
      done: (r) => `“${r.presetName}” written to ${onuSerial}.`,
      invalidate: [['onu', onuId], ['onu', onuId, 'omci-config'], ...(invalidate ?? [])],
    });
  };

  const blocked = (plan?.blockers.length ?? 0) > 0;

  return (
    <WizardDialog
      title="Apply a device preset"
      subtitle={`Writes a saved device-side setup to ${onuSerial} and to nothing else.`}
      maxWidth="2xl"
      // Configure → Review pills, matching the OMCI/TR-069 management-mode look;
      // Review is reached once the dry-run plan has been built.
      steps={[
        { key: 'configure', title: 'Configure' },
        { key: 'review', title: 'Review' },
      ]}
      currentStep={plan ? 1 : 0}
      onClose={onClose}
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="secondary"
            disabled={!presetId}
            loading={check.isPending}
            onClick={() => check.mutate()}
          >
            Preview
          </Button>
          {/* Nothing is sent until the plan has been seen, and never while a
              blocker stands — the server refuses it too, this just says so
              before the operator waits for a session. */}
          <Button
            variant="primary"
            disabled={!plan || blocked || !plan.applicable}
            title={plan ? undefined : 'Preview it first'}
            onClick={startApply}
          >
            Apply
          </Button>
        </div>
      }
    >
      {error && <ErrorNotice error={error} />}

          {presets.isLoading ? (
            <div className="flex justify-center p-6">
              <Spinner />
            </div>
          ) : items.length === 0 ? (
            <p className="text-sm text-muted">
              There are no device presets yet.{' '}
              <Link className="text-link" to="/settings/device-presets">
                Create one
              </Link>{' '}
              and it will be offered here.
            </p>
          ) : (
            <>
              <Field
                label="Preset"
                hint={
                  chosen
                    ? `Written over ${chosen.target === 'tr069' ? 'TR-069, through the ACS' : 'OMCI, through the OLT'}.`
                    : undefined
                }
              >
                <select
                  className="input"
                  value={presetId}
                  onChange={(e) => {
                    setPresetId(e.target.value);
                    setPlan(null);
                  }}
                >
                  {items.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} — {p.target === 'tr069' ? 'TR-069' : 'OMCI'}
                      {p.vendor ? ` · ${p.vendor.toUpperCase()}` : ''}
                    </option>
                  ))}
                </select>
              </Field>

              {chosen && managementMode && chosen.target !== managementMode && (
                <p className="rounded-lg border border-warn/30 bg-warn-soft px-3 py-2 text-sm text-warn">
                  This ONU is managed over {managementMode === 'tr069' ? 'TR-069' : 'OMCI'} and this
                  preset is written over {chosen.target === 'tr069' ? 'TR-069' : 'OMCI'}. Preview it
                  — the plan says whether that works here.
                </p>
              )}

              {plan && (
                <div className="space-y-3">
                  {plan.blockers.map((b) => (
                    <p key={b} className="rounded-lg border border-danger/30 bg-danger-soft px-3 py-2 text-sm text-danger">
                      {b}
                    </p>
                  ))}
                  {plan.warnings.map((w) => (
                    <p key={w} className="rounded-lg border border-warn/30 bg-warn-soft px-3 py-2 text-sm text-warn">
                      {w}
                    </p>
                  ))}

                  {/* Lead with what the operator cares about, in plain words. */}
                  {plan.summary.length > 0 && (
                    <div>
                      <p className="mb-1 text-xs uppercase tracking-wide text-faint">This changes</p>
                      <ul className="space-y-1 text-sm text-ink">
                        {plan.summary.map((s) => (
                          <li key={s}>· {s}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {plan.wan && (
                    <p className="text-sm text-muted">
                      The WAN is rebuilt as <Pill tone="info">{plan.wan.mode.toUpperCase()}</Pill>
                      {plan.wan.vlanId ? ` on VLAN ${plan.wan.vlanId}` : ''}
                      {plan.wan.pppoeUsername ? ` for ${plan.wan.pppoeUsername}` : ''}. The
                      connection renegotiates — a brief drop.
                    </p>
                  )}

                </div>
              )}
            </>
          )}
    </WizardDialog>
  );
}
